import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Table, Button, Icon, Popup, TextArea,
} from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { LoadingSpinner, FileDropzone, ConfirmDialog, EvidenceTextButton } from "../../../Shared";
import { buildStandardDashboardPdf, drawPill, drawProgressCell } from "../../../Shared/pdfCharts";
import { ActionPlan, Sector, Area, User, Site, Company } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage, useCompany } from "../../../../contexts";
import { useSite } from "../../../../contexts/SiteContext";
import { EscalationRulesModal } from "../EscalationRulesModal/EscalationRulesModal";
import { NotificationHistoryModal } from "../NotificationHistoryModal/NotificationHistoryModal";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ActionPlansTable.scss";

const CURRENT_YEAR = new Date().getFullYear();

const AVATAR_COLORS = ["#1A5FA0", "#0E376E", "#3D6B9E", "#648EBC", "#4F7FB5", "#2A4F80", "#8AB3D4"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const actionPlanController = new ActionPlan();
const sectorController     = new Sector();
const areaController       = new Area();
const userController       = new User();
const siteCtrl             = new Site();
const companyCtrl          = new Company();

const PRIORITY_OPTIONS = [
  { key: "high",   value: "high",   label: "Alta — Urgente (riesgo grave / incumplimiento legal)", color: "#e53935" },
  { key: "medium", value: "medium", label: "Media — Normal",            color: "#f9a825" },
  { key: "low",    value: "low",    label: "Baja — Mejora progresiva",  color: "#43a047" },
];

const STATUS_OPTIONS = [
  { key: "pending",              value: "pending",              label: "Pendiente"              },
  { key: "in_progress",         value: "in_progress",         label: "En progreso"            },
  { key: "implemented_pending", value: "implemented_pending", label: "Verificación pendiente" },
  { key: "closed",              value: "closed",              label: "Cerrada"                },
  { key: "overdue",             value: "overdue",             label: "Atrasada"               },
  { key: "cancelled",           value: "cancelled",           label: "Cancelada"              },
];

const ALERT_DAYS_OPTIONS = [
  { key: "7", value: 7, label: "7 días antes" },
  { key: "5", value: 5, label: "5 días antes" },
  { key: "3", value: 3, label: "3 días antes" },
  { key: "1", value: 1, label: "1 día antes" },
  { key: "0", value: 0, label: "El mismo día de vencimiento" },
];

const FINDING_ORIGIN_OPTIONS = [
  { key: "internal_audit",      value: "internal_audit",      label: "Auditoría interna" },
  { key: "external_audit",      value: "external_audit",      label: "Auditoría externa / cliente" },
  { key: "plant_inspection",    value: "plant_inspection",    label: "Inspección de planta" },
  { key: "risk_assessment",     value: "risk_assessment",     label: "Evaluación de riesgos" },
  { key: "accident_incident",   value: "accident_incident",   label: "Accidente / Incidente" },
  { key: "legal_requirement",   value: "legal_requirement",   label: "Requisito legal incumplido" },
  { key: "safety_checklist",    value: "safety_checklist",    label: "Check list de seguridad" },
  { key: "new_project",         value: "new_project",         label: "Nuevo proyecto / obra" },
  { key: "initial_diagnosis",   value: "initial_diagnosis",   label: "Diagnóstico inicial" },
  { key: "own_initiative",      value: "own_initiative",      label: "Iniciativa propia" },
  { key: "client_requirement",  value: "client_requirement",  label: "Requerimiento de cliente o marca" },
  { key: "other",               value: "other",               label: "Otro" },
];

const FINDING_TYPE_OPTIONS = [
  { key: "non_conformity",      value: "non_conformity",      label: "No conformidad" },
  { key: "observation",         value: "observation",         label: "Observación" },
  { key: "improvement",         value: "improvement",         label: "Mejora" },
  { key: "preventive_action",   value: "preventive_action",   label: "Acción preventiva" },
  { key: "corrective_action",   value: "corrective_action",   label: "Acción correctiva" },
  { key: "legal_compliance",    value: "legal_compliance",    label: "Acción de cumplimiento legal" },
];

const EMPTY = {
  title: "", findingOrigin: "", findingType: "", findingDescription: "",
  sector: "", priority: "medium",
  responsible: "", plannedStartDate: "", dueDate: "", status: "pending",
  progress: 0, alertResponsible: null, observations: [], site: "",
};

const InlineSelect = ({ options, value, onChange, placeholder }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className="apt__inline-select">
    <option value="">{placeholder || "-"}</option>
    {options.map((o) => <option key={o.key} value={o.value}>{o.label}</option>)}
  </select>
);

const FilterDropdown = ({ options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="apt__fd" ref={ref}>
      <button type="button" className="apt__fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "apt__fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="apt__fd-icons">
          {selected && (
            <span
              className="apt__fd-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`apt__fd-arrow${open ? " apt__fd-arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="apt__fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`apt__fd-item${o.value === value ? " apt__fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Dropdown multi-select (checkboxes) — misma altura/estilo de pill que
// FilterDropdown, para que ESTADO quede alineado con SITIO/PRIORIDAD/ÁREA
// en vez del bloque de checkboxes suelto que desnivelaba la fila de filtros.
const MultiSelectDropdown = ({ items, selected, onToggle, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const activeCount = items.filter((i) => selected[i.key]).length;
  const label = activeCount === 0 || activeCount === items.length
    ? placeholder
    : `${placeholder} (${activeCount})`;

  return (
    <div className="apt__fd" ref={ref}>
      <button type="button" className="apt__fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span>{label}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`apt__fd-arrow${open ? " apt__fd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className="apt__fd-menu apt__fd-menu--checks">
          {items.map((i) => (
            <label key={i.key} className="apt__fd-check-item">
              <input
                type="checkbox"
                checked={!!selected[i.key]}
                onChange={() => onToggle(i.key)}
                style={{ accentColor: i.color }}
              />
              <span style={{ color: i.color }}>{i.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const optLabel  = (opts, val) => opts.find((o) => o.value === val)?.label || val || "-";
const formatDate = (d) => d ? new Date(d).toLocaleDateString("es-AR") : "-";

function daysDiff(dueDate) {
  if (!dueDate) return null;
  const now  = new Date(); now.setHours(0, 0, 0, 0);
  const due  = new Date(dueDate); due.setHours(0, 0, 0, 0);
  return Math.round((due - now) / 86400000);
}

const PRIORITY_SHORT = { high: "Alta", medium: "Media", low: "Baja" };

function computeEscalationLevel(item) {
  if (["closed", "cancelled"].includes(item.status)) return 0;
  const diff = daysDiff(item.dueDate);
  if (diff === null) return 0;
  const l1 = item.rule_l1 ?? 0;
  const l2 = item.rule_l2 ?? 3;
  const l3 = item.rule_l3 ?? 7;
  const overdue = -diff;
  if (overdue >= l3) return 3;
  if (overdue >= l2) return 2;
  if (diff <= l1)    return 1;
  return 0;
}

function KPICard({ label, value, subtitle }) {
  return (
    <div className="apt__kpi-card">
      <div className="apt__kpi-head">
        <span className="apt__kpi-icon">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><path d="M6 10 10 6M6 6h4v4"/></svg>
        </span>
        <span className="apt__kpi-label">{label}</span>
      </div>
      <span className="apt__kpi-value">{value}</span>
      {subtitle && <span className="apt__kpi-subtitle">{subtitle}</span>}
    </div>
  );
}

function PriorityBadge({ value }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const colors = { high: "#e53935", medium: "#f9a825", low: "#43a047" };
  const c = colors[value] || "#888";
  return (
    <span
      className="apt__priority-badge"
      style={{
        background: `${c}18`,
        color: c,
        border: `1px solid ${c}35`,
      }}
    >
      {t(`priority_${value}_short`)}
    </span>
  );
}

const ESCALATION_COLORS = {
  1: "#2980b9",
  2: "#e67e22",
  3: "#c0392b",
};

function EscalationIndicator({ level = 0 }) {
  const color = ESCALATION_COLORS[level] || "#c0392b";
  return (
    <div className="apt__esc-indicator">
      {[1, 2, 3].map((lv, idx) => (
        <React.Fragment key={lv}>
          <div
            className="apt__esc-dot"
            style={level >= lv ? { background: color } : {}}
          />
          {idx < 2 && (
            <div
              className="apt__esc-line"
              style={level > lv ? { background: color } : {}}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function StatusBadge({ value }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const colorMap = {
    pending:              "#f9a825",
    in_progress:          "#1976d2",
    implemented_pending:  "#7b1fa2",
    closed:               "#43a047",
    overdue:              "#e53935",
    cancelled:            "#757575",
  };
  return (
    <span className="apt__status-badge" style={{ background: colorMap[value] || "#888" }}>
      {t(`ap_status_${value}`)}
    </span>
  );
}

export function ActionPlansTable({ reload, onReload, onEdit, triggerNew = 0, year = CURRENT_YEAR }) {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const { site: contextSite } = useSite();
  const { company: companyFromContext } = useCompany();
  const t = (key) => translations[key] || key;
  const [searchParams, setSearchParams] = useSearchParams();

  const PRIORITY_OPTIONS_I18N = [
    { key: "high",   value: "high",   label: t("priority_high").replace(/\s*\(.*\)\s*$/, ""), color: "#e53935" },
    { key: "medium", value: "medium", label: t("priority_medium"), color: "#f9a825" },
    { key: "low",    value: "low",    label: t("priority_low"),    color: "#43a047" },
  ];
  const STATUS_OPTIONS_I18N = [
    { key: "pending",              value: "pending",              label: t("ap_status_pending")             },
    { key: "in_progress",         value: "in_progress",         label: t("ap_status_in_progress")         },
    { key: "implemented_pending", value: "implemented_pending", label: t("ap_status_implemented_pending") },
    { key: "closed",              value: "closed",              label: t("ap_status_closed")              },
    { key: "overdue",             value: "overdue",             label: t("ap_status_overdue")             },
    { key: "cancelled",           value: "cancelled",           label: t("ap_status_cancelled")           },
  ];
  const ALERT_DAYS_OPTIONS_I18N = [
    { key: "7", value: 7, label: t("alert_days_7_before") },
    { key: "5", value: 5, label: t("alert_days_5_before") },
    { key: "3", value: 3, label: t("alert_days_3_before") },
    { key: "1", value: 1, label: t("alert_days_1_before") },
    { key: "0", value: 0, label: t("alert_same_day")      },
  ];
  const FINDING_ORIGIN_I18N = [
    { key: "internal_audit",     value: "internal_audit",     label: t("origin_internal_audit")     },
    { key: "external_audit",     value: "external_audit",     label: t("origin_external_audit")     },
    { key: "plant_inspection",   value: "plant_inspection",   label: t("origin_plant_inspection")   },
    { key: "risk_assessment",    value: "risk_assessment",    label: t("origin_risk_assessment")    },
    { key: "accident_incident",  value: "accident_incident",  label: t("origin_accident_incident")  },
    { key: "legal_requirement",  value: "legal_requirement",  label: t("origin_legal_requirement")  },
    { key: "safety_checklist",   value: "safety_checklist",   label: t("origin_safety_checklist")   },
    { key: "new_project",        value: "new_project",        label: t("origin_new_project")        },
    { key: "initial_diagnosis",  value: "initial_diagnosis",  label: t("origin_initial_diagnosis")  },
    { key: "own_initiative",     value: "own_initiative",     label: t("origin_own_initiative")     },
    { key: "client_requirement", value: "client_requirement", label: t("origin_client_requirement") },
    { key: "other",              value: "other",              label: t("origin_other")              },
  ];
  const FINDING_TYPE_I18N = [
    { key: "non_conformity",    value: "non_conformity",    label: t("finding_type_non_conformity")    },
    { key: "observation",       value: "observation",       label: t("finding_type_observation")       },
    { key: "improvement",       value: "improvement",       label: t("finding_type_improvement")       },
    { key: "preventive_action", value: "preventive_action", label: t("finding_type_preventive_action") },
    { key: "corrective_action", value: "corrective_action", label: t("finding_type_corrective_action") },
    { key: "legal_compliance",  value: "legal_compliance",  label: t("finding_type_legal_compliance")  },
  ];

  const isFirstRender = useRef(true);
  const importCsvRef  = useRef(null);

  const [items,       setItems]       = useState(null);
  const [itemsFilter, setItemsFilter] = useState([]);
  const [search,      setSearch]      = useState("");
  const [sectors,     setSectors]     = useState([]);
  const [areas,       setAreas]       = useState([]);
  const [catUsers,    setCatUsers]    = useState([]);

  // Escalation center
  const [showEscRules,     setShowEscRules]     = useState(false);
  const [showNotifHistory, setShowNotifHistory] = useState(false);
  const [escExpanded,      setEscExpanded]      = useState(false);
  const [triggering,       setTriggering]       = useState(false);
  const [showEscMenu,      setShowEscMenu]      = useState(false);
  const escMenuRef = useRef(null);

  useEffect(() => {
    if (!showEscMenu) return;
    const handler = (e) => {
      if (escMenuRef.current && !escMenuRef.current.contains(e.target)) setShowEscMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEscMenu]);

  const handleTriggerEscalation = async () => {
    setTriggering(true);
    try {
      const res = await actionPlanController.triggerEscalation(accessToken);
      toast.success(
        `Escalación ejecutada — ${res.notified ?? 0} notificaciones enviadas, ` +
        `${res.skipped ?? 0} omitidas`
      );
      onReload();
    } catch (err) {
      toast.error(err?.msg || "Error al ejecutar escalación");
    } finally {
      setTriggering(false);
    }
  };

  // Filters
  const [filterStatus,   setFilterStatus]   = useState({
    pending: true, in_progress: true, implemented_pending: true,
    closed: true, overdue: true, cancelled: true,
  });
  const [filterPriority, setFilterPriority] = useState("");
  const [filterArea,     setFilterArea]     = useState("");
  const [filterFrom,     setFilterFrom]     = useState("");
  const [filterTo,       setFilterTo]       = useState("");
  const [siteFilter,      setSiteFilter]      = useState("");
  const [sites,           setSites]           = useState([]);
  const [companySiteIds,  setCompanySiteIds]  = useState([]);
  const [companyName,     setCompanyName]     = useState("");
  const noSiteInCtx    = !contextSite        || contextSite        === "0" || contextSite        === 0;
  const noCompanyInCtx = !companyFromContext || companyFromContext === "0" || companyFromContext === 0;

  // Inline editing
  const [editingId,     setEditingId]     = useState(null);
  const [editingData,   setEditingData]   = useState(EMPTY);
  const [saving,        setSaving]        = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // New observation text (inside section panel)
  const [newObsText, setNewObsText] = useState("");

  // Progress modal
  const [progressModalItem, setProgressModalItem] = useState(null);
  const [progressModalVal,  setProgressModalVal]  = useState(0);
  const [savingProgress,    setSavingProgress]    = useState(false);

  // Confirm dialog
  const [selectedItem, setSelectedItem] = useState(null);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [isDelete,     setIsDelete]     = useState(false);

  // File modals
  const [showAttach,   setShowAttach]   = useState(false);
  const [showFiles,    setShowFiles]    = useState(false);
  const [fileItem,     setFileItem]     = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [deletingFile, setDeletingFile] = useState(null);
  const fileInputRef = useRef(null);

  // Observations modal
  const [showObs,        setShowObs]        = useState(false);
  const [obsItem,        setObsItem]        = useState(null);
  const [obsVal,         setObsVal]         = useState("");
  const [editingObsIdx,  setEditingObsIdx]  = useState(null);
  const [editingObsVal,  setEditingObsVal]  = useState("");
  const [editingObsDate, setEditingObsDate] = useState("");

  useEffect(() => {
    if (!accessToken) return;
    const load = !noCompanyInCtx
      ? siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      : siteCtrl.getSites(accessToken, true);
    load.then(res => setSites(Array.isArray(res) ? res : [])).catch(() => {});
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => { setSiteFilter(""); }, [contextSite]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanySiteIds([]); return; }
    siteCtrl.getSitesByCompany(accessToken, companyFromContext, true)
      .then(res => setCompanySiteIds((res || []).map(s => s._id)))
      .catch(() => setCompanySiteIds([]));
  }, [companyFromContext, accessToken]);

  useEffect(() => {
    if (noCompanyInCtx) { setCompanyName(""); return; }
    companyCtrl.getCompany(accessToken, companyFromContext)
      .then(res => setCompanyName(res?.name || ""))
      .catch(() => setCompanyName(""));
  }, [accessToken, companyFromContext, noCompanyInCtx]);

  useEffect(() => {
    if (!contextSite) return;
    userController.getUsersBySite(accessToken, contextSite, true)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setCatUsers(list.map((u) => ({
          key:   u._id,
          value: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
          text:  `${u.firstname || ""} ${u.lastname || ""}`.trim(),
        })));
      })
      .catch(() => setCatUsers([]));
  }, [contextSite, accessToken]);

  // Trigger new from parent
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (triggerNew > 0) startNew();
  }, [triggerNew]);

  // Load catalogs
  useEffect(() => {
    (async () => {
      try {
        const [secs, ars] = await Promise.all([
          sectorController.getSectors(accessToken, true),
          areaController.getAreas(accessToken, true),
        ]);
        setSectors(secs.map((s) => ({ key: s._id, value: s._id, label: s.name })));
        setAreas(ars.map((a) => ({ key: a._id, value: a._id, label: a.name })));
      } catch {}
    })();
  }, []);

  // Load data
  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await actionPlanController.getActionPlans(accessToken, true);
        setItems(res);
        setItemsFilter(res);
      } catch {
        setItems([]);
        setItemsFilter([]);
      }
    })();
  }, [reload]);

  // Abrir un registro puntual al llegar con ?open=<id> (ej. desde el buscador
  // de evidencias, "Ver documento").
  useEffect(() => {
    if (!items) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    const target = items.find((i) => i._id === openId);
    if (target) startEdit(target);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [items, searchParams]);

  // Combined filter
  useEffect(() => {
    if (!items) return;
    const q = search.toLowerCase();
    setItemsFilter(items.filter((item) => {
      // Year filter by dueDate or plannedStartDate
      const itemYear = item.dueDate
        ? new Date(item.dueDate).getFullYear()
        : item.plannedStartDate
          ? new Date(item.plannedStartDate).getFullYear()
          : null;
      if (itemYear && itemYear !== year) return false;

      // Status filter
      const st = item.status || "pending";
      if (!filterStatus[st]) return false;

      // Priority filter
      if (filterPriority && item.priority !== filterPriority) return false;

      // Area filter
      const areaId = item.area && typeof item.area === "object" ? item.area._id : item.area;
      if (filterArea && areaId !== filterArea) return false;

      // Date range (dueDate)
      if (filterFrom && item.dueDate && new Date(item.dueDate) < new Date(filterFrom)) return false;
      if (filterTo   && item.dueDate && new Date(item.dueDate) > new Date(filterTo))   return false;

      // Text search
      if (q && !item.title?.toLowerCase().includes(q) &&
               !item.responsible?.toLowerCase().includes(q) &&
               !item.findingDescription?.toLowerCase().includes(q)) return false;

      // Site filter
      const itemSite = item.site?._id || item.site || "";
      if (!noSiteInCtx) {
        if (itemSite !== contextSite) return false;
      } else if (!noCompanyInCtx && companySiteIds.length > 0) {
        if (!companySiteIds.includes(itemSite)) return false;
        if (siteFilter && itemSite !== siteFilter) return false;
      } else if (siteFilter) {
        if (itemSite !== siteFilter) return false;
      }

      return true;
    }));
  }, [search, items, year, filterStatus, filterPriority, filterArea, filterFrom, filterTo, siteFilter, contextSite, noSiteInCtx, noCompanyInCtx, companySiteIds]);

  // KPI stats
  const kpis = useMemo(() => {
    const base = itemsFilter;
    const total      = base.length;
    const inProgress = base.filter((i) => ["pending", "in_progress", "implemented_pending"].includes(i.status)).length;
    const closed     = base.filter((i) => i.status === "closed").length;
    const overdue    = base.filter((i) => {
      if (i.status === "closed") return false;
      const d = daysDiff(i.dueDate);
      return d !== null && d < 0;
    }).length;
    const closedWithDates = base.filter((i) => i.status === "closed" && i.plannedStartDate && i.closedDate);
    const leadTime = closedWithDates.length
      ? Math.round(closedWithDates.reduce((sum, i) => {
          const start = new Date(i.plannedStartDate);
          const end   = new Date(i.closedDate);
          return sum + Math.abs(Math.round((end - start) / 86400000));
        }, 0) / closedWithDates.length)
      : 0;
    return { total, inProgress, closed, overdue, leadTime };
  }, [itemsFilter]);

  // Escalation center
  const escalationData = useMemo(() => {
    const l1 = [], l2 = [], l3 = [];
    itemsFilter.forEach((item) => {
      const lv = computeEscalationLevel(item);
      if (lv === 3) l3.push(item);
      else if (lv === 2) l2.push(item);
      else if (lv === 1) l1.push(item);
    });
    return { l1, l2, l3, all: [...l1, ...l2, ...l3] };
  }, [itemsFilter]);

  // CSV export
  const exportCSV = () => {
    const headers = ["ID", "Título", "Área", "Responsable", "Prioridad", "Vencimiento", "Estado", "Avance (%)"];
    const rows = itemsFilter.map((item) => [
      `AC-${item._id?.toString().slice(-6).toUpperCase()}`,
      item.title || "",
      item.areaFuncional || item.area?.name || "",
      item.responsible || "",
      PRIORITY_SHORT[item.priority] || item.priority || "",
      item.dueDate ? new Date(item.dueDate).toLocaleDateString("es-AR") : "",
      STATUS_OPTIONS.find((o) => o.value === item.status)?.label || item.status || "",
      item.progress ?? 0,
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `planes_accion_${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const getId = (val) => (val && typeof val === "object" ? val._id : val) || "";

  // ── Export ────────────────────────────────────
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef(null);

  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => { if (!e.target.closest(".apt__row-menu, .apt__row-menu-list")) setOpenRowMenu(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openRowMenu]);

  const toggleRowMenu = (id, e) => {
    if (openRowMenu?.id === id) { setOpenRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 260;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setOpenRowMenu({
      id,
      left: rect.right - 190,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target))
        setShowExportMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showExportMenu]);

  const exportRows = (data) => data.map((a) => ({
    "ID":           `AC-${a._id?.toString().slice(-6).toUpperCase()}`,
    "Título":       a.title || "-",
    "Hallazgo":     a.findingDescription || "-",
    "Origen":       optLabel(FINDING_ORIGIN_OPTIONS, a.findingOrigin),
    "Tipo":         optLabel(FINDING_TYPE_OPTIONS, a.findingType),
    "Sector":       a.areaFuncional || a.area?.name || "-",
    "Responsable":  a.resp1 || a.responsible || "-",
    "Prioridad":    PRIORITY_SHORT[a.priority] || a.priority || "-",
    "Vencimiento":  formatDate(a.dueDate),
    "Estado":       optLabel(STATUS_OPTIONS, a.status),
    "Escalación":   a.lastEscalationLevel ? `${t("apf_esc_level")} ${a.lastEscalationLevel}` : "-",
    "Avance (%)":   a.progress ?? 0,
  }));

  const handleExportCSV = () => {
    const data = exportRows(itemsFilter);
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csv = [headers.join(","), ...data.map((r) => headers.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(","))].join("\n");
    saveAs(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }), "plan_de_accion.csv");
  };

  const handleExportXLSX = () => {
    const data = exportRows(itemsFilter);
    if (!data.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plan de acción");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "plan_de_accion.xlsx");
  };

  const handleExportPDF = async () => {
    if (!itemsFilter.length) return;
    const siteName = !noSiteInCtx
      ? sites.find(s => s._id === contextSite)?.name
      : siteFilter ? sites.find(s => s._id === siteFilter)?.name : null;

    const PRIORITY_HEX = { high: "#e53935", medium: "#f9a825", low: "#43a047" };
    const STATUS_HEX = {
      pending: "#f9a825", in_progress: "#1976d2", implemented_pending: "#7b1fa2",
      closed: "#43a047", overdue: "#e53935", cancelled: "#757575",
    };

    const tableColumns = [
      ...(noSiteInCtx ? [{ key: "site", header: t("site"), flex: 0.9, value: (r) => r.site?.name || "-" }] : []),
      { key: "id", header: "ID", flex: 0.8, value: (r) => `AC-${r._id?.toString().slice(-6).toUpperCase()}` },
      { key: "title", header: t("title_description"), flex: 1.7, value: (r) => r.title || "-" },
      { key: "area", header: t("area"), flex: 1.1, value: (r) => r.areaFuncional || r.area?.name || "-" },
      { key: "responsible", header: t("responsible"), flex: 1, value: (r) => r.resp1 || r.responsible || "-" },
      {
        key: "priority", header: t("priority"), flex: 0.9, align: "center",
        render: (doc, { x, y, width, height, row }) => {
          if (!row.priority) return;
          drawPill(doc, { x: x + width / 2, y: y + height / 2, text: t(`priority_${row.priority}_short`) || row.priority, color: PRIORITY_HEX[row.priority] || "#888" });
        },
      },
      { key: "dueDate", header: t("due_date"), flex: 1, value: (r) => formatDate(r.dueDate) },
      {
        key: "status", header: t("status"), flex: 1.1, align: "center",
        render: (doc, { x, y, width, height, row }) => {
          if (!row.status) return;
          drawPill(doc, { x: x + width / 2, y: y + height / 2, text: t(`ap_status_${row.status}`) || row.status, color: STATUS_HEX[row.status] || "#888" });
        },
      },
      {
        key: "progress", header: t("progress"), flex: 1.2,
        render: (doc, { x, y, width, height, row }) => {
          drawProgressCell(doc, { x, y, width, height, pct: row.progress || 0 });
        },
      },
    ];

    await buildStandardDashboardPdf({
      jsPDFCtor: jsPDF,
      pageTitle: t("action_plan") || "Plan de Acción",
      companyName: [companyName, siteName].filter(Boolean).join(" · "),
      generatedOnLabel: t("generated_on"),
      table: { columns: tableColumns, rows: itemsFilter, rowHeight: 24, headerHeight: 22, emptyMessage: t("no_results") },
      tableTitle: t("action_plan") || "Plan de Acción",
      filename: "plan_de_accion.pdf",
    });
  };

  const startNew = () => {
    setEditingId("new");
    setEditingData({ ...EMPTY, site: contextSite || "" });
    setActiveSection("finding");
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setEditingData({
      title:              item.title              || "",
      findingOrigin:      item.findingOrigin      || "",
      findingType:        item.findingType        || "",
      findingDescription: item.findingDescription || "",
      sector:             getId(item.sector),

      priority:           item.priority           || "medium",
      responsible:        item.responsible        || "",
      plannedStartDate:   item.plannedStartDate   ? item.plannedStartDate.slice(0, 10) : "",
      dueDate:            item.dueDate            ? item.dueDate.slice(0, 10)          : "",
      status:             item.status             || "pending",
      progress:           item.progress           ?? 0,
      alertResponsible:   item.alertResponsible   ?? null,
      observations:       item.observations       || [],
      site:               contextSite             || getId(item.site),
    });
    setActiveSection("finding");
  };

  const cancelEdit = () => { setEditingId(null); setEditingData(EMPTY); setActiveSection(null); setNewObsText(""); };
  const setField   = (field, value) => setEditingData((p) => ({ ...p, [field]: value }));

  const handleSave = async () => {
    if (!editingData.title) { toast.error(t("title_required")); return; }
    setSaving(true);
    try {
      if (editingId === "new") {
        await actionPlanController.createActionPlan(accessToken, editingData);
        toast.success(t("action_plan_created"));
      } else {
        await actionPlanController.updateActionPlan(accessToken, editingId, editingData);
        toast.success(t("action_plan_updated"));
      }
      cancelEdit();
      onReload();
    } catch (err) {
      toast.error(err?.msg || t("error_saving"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async (item) => {
    try {
      const isClosed = item.status === "closed";
      await actionPlanController.updateActionPlan(accessToken, item._id, {
        status:     isClosed ? "in_progress" : "closed",
        closedDate: isClosed ? "" : new Date().toISOString(),
        progress:   isClosed ? item.progress : 100,
      });
      toast.success(isClosed ? t("action_plan_reopened") : t("action_plan_closed"));
      onReload();
    } catch (err) {
      toast.error(err?.msg || t("error_saving"));
    }
  };

  const openProgressModal = (item) => {
    setProgressModalItem(item);
    setProgressModalVal(item.progress ?? 0);
  };

  const handleProgressSave = async () => {
    const num = Math.min(100, Math.max(0, Number(progressModalVal)));
    setSavingProgress(true);
    try {
      await actionPlanController.updateActionPlan(accessToken, progressModalItem._id, { progress: num });
      toast.success(t("action_plan_updated"));
      setProgressModalItem(null);
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSavingProgress(false); }
  };

  const openDelete = (item) => { setSelectedItem(item); setIsDelete(true);  setShowConfirm(true); };
  const handleConfirm = async () => {
    try {
      await actionPlanController.deleteActionPlan(accessToken, selectedItem._id);
      toast.success(t("action_plan_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_saving")); }
  };

  const openObs    = (item) => { setObsItem(item); setObsVal(""); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); setShowObs(true); };
  const openAttach = (item) => { setFileItem(item); setShowAttach(true); };
  const openFiles  = (item) => { setFileItem(item); setShowFiles(true); };

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSaving(true);
    try {
      const userName = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
      const updatedObs = [...(obsItem.observations || []), { text: obsVal.trim(), userName, createdAt: new Date() }];
      await actionPlanController.updateActionPlan(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      setObsVal("");
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSaving(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).map((o, i) =>
        i === editingObsIdx
          ? { ...o, text: editingObsVal.trim(), createdAt: editingObsDate ? new Date(editingObsDate) : o.createdAt }
          : o
      );
      await actionPlanController.updateActionPlan(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate("");
    } catch { toast.error(t("error_saving")); }
    finally { setSaving(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    setSaving(true);
    try {
      const updatedObs = (obsItem.observations || []).filter((_, i) => i !== origIdx);
      await actionPlanController.updateActionPlan(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSaving(false); }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        await actionPlanController.uploadFile(accessToken, fileItem._id, formData);
      }
      toast.success(t("files_uploaded"));
      setShowAttach(false);
      onReload();
    } catch { toast.error(t("error_uploading_files")); }
    finally { setUploading(false); }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFile(fileKey);
    try {
      await actionPlanController.deleteFile(accessToken, fileItem._id, fileKey);
      toast.success(t("file_deleted"));
      setFileItem((prev) => ({ ...prev, files: prev.files.filter((f) => f.key !== fileKey) }));
      onReload();
    } catch { toast.error(t("error_deleting")); }
    finally { setDeletingFile(null); }
  };

  const handleViewFile = async (fileKey, fileName) => {
    try {
      const blobUrl = await actionPlanController.getFile(accessToken, fileKey);
      if (!blobUrl) { toast.error(t("error_downloading_file")); return; }
      window.open(blobUrl, "_blank");
    } catch { toast.error(t("error_downloading_file")); }
  };

  const SECTIONS = [
    { key: "finding",   label: t("finding")   },
    { key: "followup",  label: t("followup")  },
  ];

  const renderEditRow = () => (
    <Table.Row key="editing" positive={editingId === "new"} warning={editingId !== "new"}>
      {/* ID — vacío en edición inline */}
      <Table.Cell style={{ color: "#bbb", fontSize: "0.78rem" }}>
        {editingId !== "new" ? `AC-${editingId.toString().slice(-6).toUpperCase()}` : "—"}
      </Table.Cell>
      <Table.Cell>
        <input
          type="text" value={editingData.title}
          onChange={(e) => setField("title", e.target.value)}
          placeholder={t("title")} className="apt__inline-input"
        />
      </Table.Cell>
      {/* Área — solo lectura en edición inline */}
      <Table.Cell style={{ color: "#bbb", fontSize: "0.85rem" }}>—</Table.Cell>
      <Table.Cell>
        <InlineSelect
          options={catUsers}
          value={editingData.responsible}
          onChange={(v) => setField("responsible", v)}
          placeholder={t("responsible")}
        />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={PRIORITY_OPTIONS_I18N} value={editingData.priority} onChange={(v) => setField("priority", v)} placeholder={t("priority")} />
      </Table.Cell>
      <Table.Cell>
        <input type="date" value={editingData.dueDate} onChange={(e) => setField("dueDate", e.target.value)} className="apt__inline-input" />
      </Table.Cell>
      <Table.Cell>
        <InlineSelect options={STATUS_OPTIONS_I18N} value={editingData.status} onChange={(v) => setField("status", v)} />
      </Table.Cell>
      {/* Escalación — solo lectura en edición inline */}
      <Table.Cell style={{ color: "#bbb", fontSize: "0.85rem" }}>—</Table.Cell>
      <Table.Cell>
        <input
          type="number" min={0} max={100}
          value={editingData.progress}
          onChange={(e) => setField("progress", Number(e.target.value))}
          className="apt__inline-input" style={{ width: "60px" }}
        />
        <span style={{ marginLeft: "4px", fontSize: "0.82rem", color: "#555" }}>%</span>
      </Table.Cell>
      <Table.Cell textAlign="center" className="apt__actions">
        <Popup size="mini" content={t("save")} trigger={
          <Button icon size="mini" loading={saving} disabled={saving} onClick={handleSave} className="apt__save-btn">
            <Icon name="save" />
          </Button>
        } />
        <Popup size="mini" content={t("cancel")} trigger={
          <Button icon size="mini" onClick={cancelEdit} disabled={saving}>
            <Icon name="close" />
          </Button>
        } />
      </Table.Cell>
    </Table.Row>
  );

  const renderSectionPanel = () => {
    if (!activeSection || !editingId) return null;

    const panelContent = {
      finding: (
        <div className="apt__section-fields">
          <div className="apt__field-grid">
            <div className="apt__field-row">
              <label>{t("title")} *</label>
              <input type="text" value={editingData.title} onChange={(e) => setField("title", e.target.value)} className="apt__inline-input" />
            </div>
            <div className="apt__field-row">
              <label>{t("finding_origin")}</label>
              <InlineSelect options={FINDING_ORIGIN_I18N} value={editingData.findingOrigin} onChange={(v) => setField("findingOrigin", v)} placeholder={t("select")} />
            </div>
            <div className="apt__field-row">
              <label>{t("finding_type")}</label>
              <InlineSelect options={FINDING_TYPE_I18N} value={editingData.findingType} onChange={(v) => setField("findingType", v)} placeholder={t("select")} />
            </div>
            <div className="apt__field-row">
              <label>{t("sector")}</label>
              <InlineSelect options={sectors} value={editingData.sector} onChange={(v) => setField("sector", v)} placeholder={t("select")} />
            </div>

            <div className="apt__field-row">
              <label>{t("priority")}</label>
              <InlineSelect options={PRIORITY_OPTIONS_I18N} value={editingData.priority} onChange={(v) => setField("priority", v)} />
            </div>
          </div>
          <div className="apt__field-row" style={{ gridColumn: "1 / -1" }}>
            <label>{t("finding_description")}</label>
            <textarea value={editingData.findingDescription} onChange={(e) => setField("findingDescription", e.target.value)} className="apt__inline-textarea" rows={4} />
          </div>
        </div>
      ),
      followup: (
        <div className="apt__section-fields">
          <div className="apt__field-grid">
            <div className="apt__field-row">
              <label>{t("responsible")}</label>
              <InlineSelect options={catUsers} value={editingData.responsible} onChange={(v) => setField("responsible", v)} placeholder={t("responsible")} />
            </div>
            <div className="apt__field-row">
              <label>{t("planned_start_date")}</label>
              <input type="date" value={editingData.plannedStartDate} onChange={(e) => setField("plannedStartDate", e.target.value)} className="apt__inline-input" />
            </div>
            <div className="apt__field-row">
              <label>{t("due_date")}</label>
              <input type="date" value={editingData.dueDate} onChange={(e) => setField("dueDate", e.target.value)} className="apt__inline-input" />
            </div>
            <div className="apt__field-row">
              <label>{t("status")}</label>
              <InlineSelect options={STATUS_OPTIONS_I18N} value={editingData.status} onChange={(v) => setField("status", v)} />
            </div>
            <div className="apt__field-row">
              <label>{t("progress")} (%)</label>
              <input type="number" min={0} max={100} value={editingData.progress} onChange={(e) => setField("progress", Number(e.target.value))} className="apt__inline-input" style={{ width: "80px" }} />
            </div>
            <div className="apt__field-row">
              <label>{t("alert_responsible")}</label>
              <div className="apt__alert-row">
                <label className="apt__toggle">
                  <input
                    type="checkbox"
                    checked={editingData.alertResponsible !== null && editingData.alertResponsible !== undefined}
                    onChange={(e) => setField("alertResponsible", e.target.checked ? 7 : null)}
                  />
                  <span className="apt__toggle-slider" />
                </label>
                {editingData.alertResponsible !== null && editingData.alertResponsible !== undefined && (
                  <InlineSelect
                    options={ALERT_DAYS_OPTIONS_I18N}
                    value={editingData.alertResponsible}
                    onChange={(v) => setField("alertResponsible", Number(v))}
                    placeholder="-"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Evidencia de cierre */}
          <div className="apt__field-row" style={{ marginTop: "0.5rem" }}>
            <label>{t("closing_evidence")}</label>
            {editingId === "new" ? (
              <p className="apt__evidence-hint">{t("save_first_to_attach")}</p>
            ) : (
              <div className="apt__evidence-section">
                {/* Archivos existentes */}
                {(() => {
                  const currentItem = items?.find((i) => i._id === editingId);
                  const files = currentItem?.files || [];
                  return files.length > 0 ? (
                    <ul className="apt__evidence-list">
                      {files.map((f, i) => (
                        <li key={i} className="apt__evidence-item">
                          <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} />
                          <span className="apt__evidence-name">{f.name}</span>
                          <Button circular icon size="mini" style={{ backgroundColor: "#648EBC", color: "#fff" }}
                            onClick={() => handleViewFile(f.key, f.name)}>
                            <Icon name="eye" />
                          </Button>
                          <Button circular icon size="mini" style={{ backgroundColor: "#e57373", color: "#fff" }}
                            loading={deletingFile === f.key} disabled={deletingFile !== null}
                            onClick={async () => {
                              setDeletingFile(f.key);
                              try {
                                await actionPlanController.deleteFile(accessToken, editingId, f.key);
                                toast.success(t("file_deleted"));
                                onReload();
                              } catch { toast.error(t("error_deleting")); }
                              finally { setDeletingFile(null); }
                            }}>
                            <Icon name="trash" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="apt__evidence-hint">{t("no_attached_files")}</p>
                  );
                })()}
                {/* Upload */}
                <div className="apt__evidence-upload">
                  <input type="file" multiple ref={fileInputRef} />
                  <Button size="mini" primary loading={uploading} disabled={uploading}
                    onClick={async () => {
                      const files = fileInputRef.current?.files;
                      if (!files || files.length === 0) { toast.warning(t("select_files_to_attach")); return; }
                      setUploading(true);
                      try {
                        for (const file of Array.from(files)) {
                          const formData = new FormData();
                          formData.append("file", file);
                          await actionPlanController.uploadFile(accessToken, editingId, formData);
                        }
                        toast.success(t("files_uploaded"));
                        if (fileInputRef.current) fileInputRef.current.value = "";
                        onReload();
                      } catch { toast.error(t("error_uploading_files")); }
                      finally { setUploading(false); }
                    }}>
                    {t("upload")}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Observaciones */}
          <div className="apt__field-row" style={{ marginTop: "0.5rem" }}>
            <label>{t("observations")}</label>
            {editingData.observations.length > 0 && (
              <div className="apt__obs-list">
                {[...editingData.observations].reverse().map((obs, i) => (
                  <div key={i} className="apt__obs-entry">
                    <div className="apt__obs-meta">
                      <span className="apt__obs-user">{obs.userName}</span>
                      <span className="apt__obs-date">{obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}</span>
                      <Icon
                        name="trash alternate"
                        link
                        color="red"
                        style={{ marginLeft: "auto" }}
                        onClick={() => {
                          const idx = editingData.observations.length - 1 - i;
                          setField("observations", editingData.observations.filter((_, j) => j !== idx));
                        }}
                      />
                    </div>
                    <div className="apt__obs-text">{obs.text}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="apt__obs-new">
              <textarea
                rows={2}
                value={newObsText}
                onChange={(e) => setNewObsText(e.target.value)}
                placeholder={t("enter_observations")}
                className="apt__inline-textarea"
              />
              <Button
                size="mini" primary
                disabled={!newObsText.trim()}
                onClick={() => {
                  const userName = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
                  setField("observations", [
                    ...editingData.observations,
                    { text: newObsText.trim(), userName, createdAt: new Date() },
                  ]);
                  setNewObsText("");
                }}
              >
                {t("add")}
              </Button>
            </div>
          </div>
        </div>
      ),
    };

    return (
      <div className="apt__section-panel">
        <div className="apt__section-nav">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button"
              className={`apt__section-btn${activeSection === s.key ? " active" : ""}`}
              onClick={() => setActiveSection(activeSection === s.key ? null : s.key)}
            >{s.label}</button>
          ))}
        </div>
        {activeSection && (
          <div className="apt__section-body">
            {panelContent[activeSection]}
          </div>
        )}
      </div>
    );
  };

  if (items === null) return <LoadingSpinner />;

  const areaOptions = [{ key: "", value: "", label: t("all_f") }, ...areas];
  const prioOptions = [{ key: "", value: "", label: t("all_f") }, ...PRIORITY_OPTIONS_I18N];

  const STATUS_FILTER_ITEMS = [
    { key: "pending",              label: t("ap_status_pending"),              color: "#f9a825" },
    { key: "in_progress",         label: t("ap_status_in_progress"),          color: "#1976d2" },
    { key: "implemented_pending", label: t("ap_status_implemented_pending"),  color: "#7b1fa2" },
    { key: "closed",              label: t("ap_status_closed"),               color: "#43a047" },
    { key: "overdue",             label: t("ap_status_overdue"),              color: "#e53935" },
    { key: "cancelled",           label: t("ap_status_cancelled"),            color: "#757575" },
  ];

  return (
    <div className="apt">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Centro de Alertas — Escalación + Descargar/Nueva acción ── */}
      <div className="apt__esc-row">
        <div className="apt__esc-bar">
          <div className="apt__esc-bar-left">
            <i className="bell icon apt__esc-bar-bell" />
            <span className="apt__esc-bar-title">{t("escalation_center_title")}</span>
            {escalationData.all.length > 0 && (
              <span className="apt__esc-bar-badge">{escalationData.all.length}</span>
            )}
          </div>
          <div className="apt__esc-bar-right">
            {/* Escritorio: botones directos */}
            <button className="apt__esc-bar-btn apt__esc-bar-btn--desktop" onClick={() => setShowNotifHistory(true)}>
              <Icon name="file alternate outline" />{t("notification_history")}
            </button>
            <button className="apt__esc-bar-btn apt__esc-bar-btn--desktop" onClick={() => setShowEscRules(true)}>
              <Icon name="setting" />{t("rules")}
            </button>
            <button
              className="apt__esc-bar-btn apt__esc-bar-btn--desktop"
              onClick={handleTriggerEscalation}
              disabled={triggering}
            >
              <Icon name="redo alternate" loading={triggering} />
              {triggering ? t("running") : t("re_trigger")}
            </button>

            {/* Mobile: mismas acciones agrupadas en un menú compacto */}
            <div className="apt__esc-menu-wrap" ref={escMenuRef}>
              <button
                type="button"
                className="apt__esc-bar-kebab"
                onClick={() => setShowEscMenu((v) => !v)}
                aria-label={t("actions")}
              >
                <Icon name="ellipsis vertical" />
              </button>
              {showEscMenu && (
                <div className="apt__esc-menu">
                  <button
                    type="button"
                    className="apt__esc-menu-item"
                    onClick={() => { setShowNotifHistory(true); setShowEscMenu(false); }}
                  >
                    <Icon name="file alternate outline" />{t("notification_history")}
                  </button>
                  <button
                    type="button"
                    className="apt__esc-menu-item"
                    onClick={() => { setShowEscRules(true); setShowEscMenu(false); }}
                  >
                    <Icon name="setting" />{t("rules")}
                  </button>
                  <button
                    type="button"
                    className="apt__esc-menu-item"
                    onClick={() => { handleTriggerEscalation(); setShowEscMenu(false); }}
                    disabled={triggering}
                  >
                    <Icon name="redo alternate" loading={triggering} />
                    {triggering ? t("running") : t("re_trigger")}
                  </button>
                </div>
              )}
            </div>

            <button
              className={`apt__esc-bar-expand${escExpanded ? " expanded" : ""}`}
              onClick={() => setEscExpanded((p) => !p)}
            >
              <Icon name="chevron right" />
            </button>
          </div>
        </div>

        {/* Descargar / Nueva acción — fuera de la caja del Centro de
            Alertas, a la derecha, compartiendo la misma fila para ocupar menos alto */}
        <div className="apt__esc-actions">
          <div className="apt__export-dropdown" ref={exportMenuRef}>
            <button className="apt__export-toggle" onClick={() => setShowExportMenu(v => !v)}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
                <path d="M3 13h10M8 3v7m0 0L5 7m3 3 3-3"/>
              </svg>
              {t("export_btn")}
              <svg viewBox="0 0 12 12" fill="currentColor" width="9" height="9"><path d="M6 8L2 4h8z"/></svg>
            </button>
            {showExportMenu && (
              <div className="apt__export-menu">
                <button className="apt__export-menu-item" onClick={() => { handleExportCSV(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  CSV
                </button>
                <button className="apt__export-menu-item" onClick={() => { handleExportXLSX(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Excel (XLSX)
                </button>
                <button className="apt__export-menu-item" onClick={() => { handleExportPDF(); setShowExportMenu(false); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  PDF
                </button>
              </div>
            )}
          </div>
          {!noSiteInCtx && (
            <button className="apt__new-btn" onClick={() => { if (onEdit) onEdit(null); else startNew(); }} title={t("new_action")}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <path d="M8 3v10M3 8h10"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Detalle escalación — expandible */}
      {escExpanded && escalationData.all.length > 0 && (
        <div className="apt__esc-detail">
          <div className="apt__esc-cards">
            {[
              { level: 1, label: t("esc_level_1_label"), desc: t("esc_level_1_desc"), items: escalationData.l1, color: "#2980b9", bg: "#eaf4fd" },
              { level: 2, label: t("esc_level_2_label"), desc: t("esc_level_2_desc"), items: escalationData.l2, color: "#e67e22", bg: "#fef5e9" },
              { level: 3, label: t("esc_level_3_label"), desc: t("esc_level_3_desc"), items: escalationData.l3, color: "#c0392b", bg: "#fdecea" },
            ].map(({ level, label, desc, items: lvItems, color, bg }) => (
              <div key={level} className="apt__esc-card" style={{ borderColor: color, background: bg }}>
                <div className="apt__esc-card-top">
                  <span className="apt__esc-card-badge-lv" style={{ background: color }}>N{level}</span>
                  <span className="apt__esc-card-label">{label}</span>
                </div>
                <div className="apt__esc-card-count" style={{ color }}>{lvItems.length}</div>
                <div className="apt__esc-card-desc">{desc}</div>
              </div>
            ))}
          </div>
          <div className="apt__esc-list">
            <table className="apt__esc-tbl">
              <thead>
                <tr><th>ID</th><th>{t("title")}</th><th>{t("responsible")}</th><th>{t("due_date")}</th><th>{t("escalation")}</th></tr>
              </thead>
              <tbody>
                {escalationData.all.map((item) => {
                  const lv   = computeEscalationLevel(item);
                  const meta = { 1: { label: "N1", color: "#2980b9" }, 2: { label: "N2", color: "#e67e22" }, 3: { label: "N3", color: "#c0392b" } };
                  return (
                    <tr key={item._id}>
                      <td style={{ color: "#888", fontSize: "0.78rem" }}>AC-{item._id?.toString().slice(-6).toUpperCase()}</td>
                      <td style={{ fontWeight: 600, textAlign: "left" }}>{item.title}</td>
                      <td>{item.responsible || "-"}</td>
                      <td>{formatDate(item.dueDate)}</td>
                      <td><EscalationIndicator level={lv} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {escExpanded && escalationData.all.length === 0 && (
        <div className="apt__esc-empty">{t("no_escalated_actions")}</div>
      )}

      <EscalationRulesModal show={showEscRules} close={() => setShowEscRules(false)} />
      <NotificationHistoryModal show={showNotifHistory} close={() => setShowNotifHistory(false)} items={items || []} />

      {/* ── Filtros + tabla, en una sola card ── */}
      <div className="apt__card">
        <div className="apt__kpi-wrapper">
          <KPICard label={t("kpi_total")}            value={kpis.total}      subtitle={t("kpi_sub_total")} />
          <KPICard label={t("kpi_in_progress_label")} value={kpis.inProgress} subtitle={t("kpi_sub_in_progress")} />
          <KPICard label={t("kpi_closed_label")}     value={kpis.closed}     subtitle={t("kpi_sub_closed")} />
          <KPICard label={t("kpi_overdue_label")}    value={kpis.overdue}    subtitle={t("kpi_sub_overdue")} />
          <KPICard label={t("kpi_lead_time")}        value={kpis.leadTime}   subtitle={t("kpi_sub_lead_time")} />
        </div>

        <div className="apt__filter-grid">
          {/* BUSCAR */}
          <div className="apt__filter-group">
            <div className="apt__search-wrapper">
              <img src={searchSvg} alt="search" className="apt__search-icon" />
              <input className="apt__search-input" placeholder={t("search_ap_placeholder")}
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          {/* SITIO */}
          {noSiteInCtx && sites.length > 0 && (
            <div className="apt__filter-group">
              <FilterDropdown
                options={sites.map(s => ({ key: s._id, value: s._id, text: s.name }))}
                value={siteFilter}
                onChange={setSiteFilter}
                placeholder={t("filter_site")}
              />
            </div>
          )}

          {/* PRIORIDAD */}
          <div className="apt__filter-group">
            <FilterDropdown
              options={prioOptions.filter(o => o.value).map(o => ({ ...o, text: o.label }))}
              value={filterPriority}
              onChange={setFilterPriority}
              placeholder={t("filter_priority")}
            />
          </div>

          {/* ÁREA */}
          <div className="apt__filter-group">
            <FilterDropdown
              options={areaOptions.filter(o => o.value).map(o => ({ ...o, text: o.label }))}
              value={filterArea}
              onChange={setFilterArea}
              placeholder={t("filter_area")}
            />
          </div>

          {/* ESTADO */}
          <div className="apt__filter-group">
            <MultiSelectDropdown
              items={STATUS_FILTER_ITEMS}
              selected={filterStatus}
              onToggle={(key) => setFilterStatus((p) => ({ ...p, [key]: !p[key] }))}
              placeholder={t("filter_status")}
            />
          </div>

          {/* DESDE */}
          <div className="apt__filter-group">
            <div className="apt__date-wrapper">
              <span className="apt__date-label">{t("filter_from_opening")}</span>
              <input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="apt__filter-date apt__filter-date--inline" />
            </div>
          </div>

          {/* HASTA */}
          <div className="apt__filter-group">
            <div className="apt__date-wrapper">
              <span className="apt__date-label">{t("filter_to_opening")}</span>
              <input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="apt__filter-date apt__filter-date--inline" />
            </div>
          </div>
        </div>

      {/* Table */}
      {itemsFilter.length === 0 && editingId !== "new" ? (
        <div className="apt__empty">{t("no_results")}</div>
      ) : (
      <div className="apt__tbl-scroll">
        <Table className="apt__tbl unstackable rtable-cards" selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell style={{ width: "70px" }}>ID</Table.HeaderCell>
              <Table.HeaderCell>{t("title_description")}</Table.HeaderCell>
              <Table.HeaderCell>{t("area")}</Table.HeaderCell>
              <Table.HeaderCell>{t("responsible")}</Table.HeaderCell>
              <Table.HeaderCell>{t("priority")}</Table.HeaderCell>
              <Table.HeaderCell>{t("due_date")}</Table.HeaderCell>
              <Table.HeaderCell>{t("status")}</Table.HeaderCell>
              <Table.HeaderCell>{t("escalation")}</Table.HeaderCell>
              <Table.HeaderCell>{t("progress")}</Table.HeaderCell>
              <Table.HeaderCell textAlign="center"></Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {itemsFilter.map((item) =>
              editingId === item._id ? renderEditRow() : (
                <React.Fragment key={item._id}>
                  <Table.Row
                    active={editingId === item._id}
                    onClick={() => { if (!editingId) { if (onEdit) onEdit(item); else startEdit(item); } }}
                    style={{ cursor: "pointer" }}
                  >
                    {/* ID */}
                    <Table.Cell data-label="ID" style={{ whiteSpace: "nowrap", fontSize: "0.78rem", color: "#888" }}>
                      AC-{item._id?.toString().slice(-6).toUpperCase()}
                    </Table.Cell>
                    {/* Title + description */}
                    <Table.Cell data-label={t("title_description")} style={{ minWidth: "200px" }}>
                      <div className="apt__title-cell">
                        <span className="apt__title-main">{item.title}</span>
                        {item.findingDescription && (
                          <span className="apt__title-desc">{item.findingDescription}</span>
                        )}
                        <span className="apt__title-dates">
                          {[
                            item.findingOrigin && optLabel(FINDING_ORIGIN_I18N, item.findingOrigin),
                            item.findingType   && optLabel(FINDING_TYPE_I18N,   item.findingType),
                            item.plannedStartDate && `${t("ap_opening_prefix")} ${formatDate(item.plannedStartDate)}`,
                            item.status === "closed" && item.closedDate && `${t("ap_closing_prefix")} ${formatDate(item.closedDate)}`,
                          ].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                    </Table.Cell>
                    {/* Área */}
                    <Table.Cell data-label={t("area")} style={{ fontSize: "0.85rem" }}>
                      {item.areaFuncional || item.area?.name || "-"}
                    </Table.Cell>
                    <Table.Cell data-label={t("responsible")}>{item.resp1 || item.responsible || "-"}</Table.Cell>
                    <Table.Cell data-label={t("priority")}><PriorityBadge value={item.priority} /></Table.Cell>
                    {/* Due date + overdue badge */}
                    <Table.Cell data-label={t("due_date")}>
                      <div className="apt__due-cell">
                        <span><Icon name="calendar outline" />{formatDate(item.dueDate)}</span>
                        {(() => {
                          const diff = daysDiff(item.dueDate);
                          if (diff === null || item.status === "closed") return null;
                          if (diff < 0) return <span className="apt__overdue-badge"><Icon name="warning sign" />{t("overdue_by")} {Math.abs(diff)} {t("days")}</span>;
                          if (diff <= 7)  return <span className="apt__warning-badge"><Icon name="clock outline" />{t("due_in")} {diff} {t("days")}</span>;
                          return null;
                        })()}
                      </div>
                    </Table.Cell>
                    <Table.Cell data-label={t("status")}><StatusBadge value={item.status} /></Table.Cell>
                    {/* Escalación */}
                    <Table.Cell data-label={t("escalation")}><EscalationIndicator level={computeEscalationLevel(item)} /></Table.Cell>
                    {/* Progress - read only */}
                    <Table.Cell data-label={t("progress")}>
                      <div className="apt__progress-cell">
                        <div className="apt__progress-bar-wrap">
                          <div
                            className="apt__progress-bar"
                            style={{
                              width: `${item.progress || 0}%`,
                              background:
                                item.status === "closed" ? "#43a047" :
                                daysDiff(item.dueDate) !== null && daysDiff(item.dueDate) < 0 ? "#e53935" :
                                "#648EBC",
                            }}
                          />
                        </div>
                        <span className="apt__progress-input apt__progress-input--static">{item.progress ?? 0}</span>
                        <span className="apt__progress-pct">%</span>
                      </div>
                    </Table.Cell>
                    {/* Acciones */}
                    <Table.Cell textAlign="center" className="apt__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="apt__row-menu">
                        <button
                          type="button"
                          className="apt__row-menu-btn"
                          onClick={(e) => toggleRowMenu(item._id, e)}
                        >
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {openRowMenu?.id === item._id && ReactDOM.createPortal(
                        <div
                          className="apt__row-menu-list"
                          style={{
                            position: "fixed",
                            top: openRowMenu.top,
                            left: openRowMenu.left,
                            transform: openRowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { (onEdit ? onEdit(item) : startEdit(item)); setOpenRowMenu(null); }} disabled={!!editingId}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openProgressModal(item); setOpenRowMenu(null); }} disabled={!!editingId}>
                            <Icon name="chart line" />{t("edit_progress")}
                          </button>
                          <button onClick={() => { openObs(item); setOpenRowMenu(null); }} disabled={!!editingId}>
                            <Icon name="comment outline" />{t("observations")}
                          </button>
                          <button onClick={() => { openAttach(item); setOpenRowMenu(null); }} disabled={!!editingId}>
                            <Icon name="attach" />{t("attach_files")}
                          </button>
                          <button onClick={() => { openFiles(item); setOpenRowMenu(null); }} disabled={!!editingId}>
                            <Icon name="folder open outline" />{t("view_files")}
                          </button>
                          <button className="apt__row-menu-item--danger" onClick={() => { openDelete(item); setOpenRowMenu(null); }} disabled={!!editingId}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                </React.Fragment>
              )
            )}
            {editingId === "new" && renderEditRow()}
          </Table.Body>
        </Table>
      </div>
      )}
      </div>

      {/* Section panel */}
      {editingId && renderSectionPanel()}

      {/* Tip */}
      <p className="apt__tip">{t("action_plan_tip")}</p>

      <ConfirmDialog
        open={showConfirm}
        title={t("delete")}
        message={`¿${t("delete")} "${selectedItem?.title}"?`}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        confirmDanger
      />

      {/* Attach files modal */}
      {showAttach && (
        <div className="ap-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowAttach(false); }}>
          <div className="ap-obs-modal">
            <div className="ap-obs-header">
              <h3 className="ap-obs-title">{t("attach_files")}</h3>
              <button className="ap-obs-close" type="button" onClick={() => setShowAttach(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="ap-obs-body">
              <FileDropzone
                onUpload={handleUpload}
                uploading={uploading}
                onCancel={() => setShowAttach(false)}
                supportedLabel={t("ap_supported_files")}
              />
            </div>
          </div>
        </div>
      )}

      {/* View files modal */}
      {showFiles && (
        <div className="ap-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowFiles(false); }}>
          <div className="ap-obs-modal">
            <div className="ap-obs-header">
              <h3 className="ap-obs-title">{t("attached_files")}</h3>
              <button className="ap-obs-close" type="button" onClick={() => setShowFiles(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="ap-obs-body">
              {!fileItem?.files?.length ? (
                <p style={{ color: "#999", textAlign: "center", padding: "1.5rem 0", margin: 0 }}>{t("no_attached_files")}</p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px" }}>
                  {fileItem.files.map((f, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #E5E7EB", marginBottom: "8px" }}>
                      <Icon name={/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? "image outline" : "file outline"} style={{ color: "#4A7FA5", margin: 0, fontSize: "1.1rem" }} />
                      <span style={{ flex: 1, fontSize: "0.875rem", color: "#374151", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</span>
                      <button title={t("view")} onClick={() => handleViewFile(f.key, f.name)}
                        style={{ border: "none", background: "#4A7FA5", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="eye" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                      <EvidenceTextButton module="actionPlan" fileKey={f.key} fileName={f.name} />
                      <button title={t("delete")} onClick={() => handleDeleteFile(f.key)} disabled={deletingFile !== null}
                        style={{ border: "none", background: deletingFile === f.key ? "#ccc" : "#E74C3C", color: "#fff", borderRadius: "50%", width: "30px", height: "30px", cursor: deletingFile !== null ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="trash" style={{ margin: 0, fontSize: "0.8rem" }} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <button onClick={() => setShowFiles(false)}
                  style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}>
                  {t("close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress modal */}
      {!!progressModalItem && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
          onClick={(e) => { if (e.target === e.currentTarget) setProgressModalItem(null); }}
        >
          <div style={{ background: "#F8F9FB", borderRadius: "20px", padding: "28px 30px 26px", width: "460px", maxWidth: "90vw", boxShadow: "0 8px 40px rgba(0,0,0,0.15)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
              <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#1A2E4A", margin: 0 }}>{t("edit_progress")}</h3>
              <button onClick={() => setProgressModalItem(null)}
                style={{ border: "none", background: "none", cursor: "pointer", fontSize: "1.2rem", color: "#666", lineHeight: 1 }}>×</button>
            </div>
            {progressModalItem?.title && (
              <p style={{ marginBottom: "1rem", fontSize: "0.88rem", color: "#555" }}>{progressModalItem.title}</p>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
              <input
                type="range" min={0} max={100}
                value={progressModalVal}
                onChange={(e) => setProgressModalVal(Number(e.target.value))}
                style={{ flex: 1, cursor: "pointer", accentColor: "#4A7FA5" }}
              />
              <input
                type="number" min={0} max={100}
                value={progressModalVal}
                onChange={(e) => setProgressModalVal(Math.min(100, Math.max(0, Number(e.target.value))))}
                style={{ width: "65px", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", textAlign: "center", fontFamily: "'Archivo', sans-serif", fontSize: "0.9rem", outline: "none" }}
              />
              <span style={{ fontSize: "0.9rem", color: "#374151", fontWeight: 600 }}>%</span>
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: "14px" }}>
              <button type="button" disabled={savingProgress} onClick={handleProgressSave}
                style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: savingProgress ? "not-allowed" : "pointer", background: "#4A7FA5", color: "#fff", opacity: savingProgress ? 0.55 : 1 }}>
                {savingProgress ? "…" : t("save")}
              </button>
              <button type="button" disabled={savingProgress} onClick={() => setProgressModalItem(null)}
                style={{ border: "none", borderRadius: "20px", padding: "10px 30px", fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif", cursor: "pointer", background: "#9BAFC4", color: "#fff" }}>
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Observations modal */}
      {showObs && ReactDOM.createPortal(
        <div className="ap-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); } }}>
          <div className="ap-obs-modal">
            <div className="ap-obs-header">
              <p className="ap-obs-title">{t("observations")}</p>
              <button className="ap-obs-close" type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="ap-obs-body">
            {obsItem?.observations?.length > 0 && (
              <div className="lr-table__obs-history">
                {[...obsItem.observations].map((obs, origIdx) => ({ obs, origIdx })).reverse().map(({ obs, origIdx }) => {
                  const currentUser = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
                  const isOwner  = obs.userName === currentUser;
                  const isEditing = editingObsIdx === origIdx;
                  return (
                    <div key={origIdx} className="lr-table__obs-entry">
                      <span className="lr-table__obs-avatar" style={{ background: avatarColor(obs.userName) }}>
                        {getInitials(obs.userName)}
                      </span>
                      <div className="lr-table__obs-body">
                        <div className="lr-table__obs-meta">
                          <span className="lr-table__obs-user">{obs.userName}</span>
                          <span className="lr-table__obs-date">{obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}</span>
                          {isOwner && !isEditing && (
                            <span className="lr-table__obs-actions">
                              <Icon name="pencil" link onClick={() => { setEditingObsIdx(origIdx); setEditingObsVal(obs.text); setEditingObsDate(obs.createdAt ? new Date(obs.createdAt).toISOString().split("T")[0] : ""); }} />
                              <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                            </span>
                          )}
                        </div>
                        {isEditing ? (
                          <div>
                            <textarea rows={2} value={editingObsVal} onChange={(e) => setEditingObsVal(e.target.value)}
                              style={{ width: "100%", marginBottom: "0.5rem", padding: "8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontFamily: "'Archivo', sans-serif", resize: "none" }} />
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button type="button" disabled={saving || !editingObsVal.trim()} onClick={handleSaveObsEdit} className="obs-icon-btn obs-icon-btn--confirm">
                                <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5L5 9.5L11 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </button>
                              <button type="button" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }} className="obs-icon-btn obs-icon-btn--cancel">
                                <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M8.5 2.5L2.5 8.5M2.5 2.5L8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="lr-table__obs-text">{obs.text}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <textarea rows={3} value={obsVal} onChange={(e) => setObsVal(e.target.value)}
              placeholder={t("enter_observations")} />
            <div className="ap-obs-actions">
              <button type="button" disabled={saving || !obsVal.trim()} onClick={handleSaveObs} className="ap-obs-btn-confirm">
                {saving ? "…" : t("save")}
              </button>
              <button type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }} className="ap-obs-btn-cancel">
                {t("cancel")}
              </button>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
